import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Apollo} from "apollo-angular";
import {Store} from "@ngrx/store";
import {exhaustMap, map, mergeMap} from "rxjs/operators";
import {StoreItemActions} from "./store-item.actions";
import {BaseResponse} from "../../base.entity";
import {NotificationService} from "../../../services/notification.service";
import {switchMap} from "rxjs";
import {AppState} from "../../index";
import _ from "lodash";
import {StoreItemEntity} from "./store-item.model";


@Injectable()
export class StoreItemEffects {

  create$ = createEffect(() => this.actions$.pipe(
      ofType(StoreItemActions.createUpdateStoreItem),
      exhaustMap((action) => {
        return this.apollo
          .mutate({
            mutation: _.cloneDeep(action?.mutation),
            variables: action?.isInput ? {input: action?.input} : {...action?.input}
          })
          .pipe(
            map(({data}: any) => {
              const result: BaseResponse<any> = Object.values(data)[0]
              if (result?.response?.status) {
                this.notificationService.successMessage(result?.response?.message)
                this.store.dispatch(StoreItemActions.upsertStoreItem({
                  storeItem: result?.data,
                  entity: action?.entity
                }));
              } else {
                this.notificationService.errorMessage(result?.response?.message);
              }
            })
          );
      })
    ),
    {dispatch: false}
  );

  preview$ = createEffect(() => this.actions$.pipe(
      ofType(StoreItemActions.previewStoreItem),
      exhaustMap((action) => {
        return this.apollo
          .mutate({
            mutation: _.cloneDeep(action?.mutation),
            variables: action?.isInput ? {input: action?.input} : {...action?.input}
          })
          .pipe(
            map(({data}: any) => {
              const result: BaseResponse<any> = Object.values(data)[0]
              if (result?.response?.status) {
                const storeItems = Array.isArray(result?.data) ? result?.data : [result?.data]
                this.store.dispatch(StoreItemActions.loadStoreItems({
                  storeItems,
                  entity: action?.entity
                }));
              } else {
                this.notificationService.errorMessage(result?.response?.message);
              }
            })
          );
      })
    ),
    {dispatch: false}
  );

  delete$ = createEffect(() => this.actions$.pipe(
      ofType(StoreItemActions.removeStoreItem),
      exhaustMap((action) => {
        return this.apollo
          .mutate({
            mutation: _.cloneDeep(action?.mutation),
            variables: action?.isInput ? {input: action?.input} : action?.input
          })
          .pipe(
            map(({data}: any) => {
              const result: BaseResponse<any> = Object.values(data)[0]
              if (result?.response?.status) {
                this.notificationService.successMessage(result?.response?.message)
                this.store.dispatch(StoreItemActions.deleteStoreItem({
                  id: action?.id,
                  entity: action?.entity
                }));
              } else {
                this.notificationService.errorMessage(result?.response?.message);
              }
            })
          );
      })
    ),
    {dispatch: false}
  );

  get$ = createEffect(() => this.actions$.pipe(
    ofType(StoreItemActions.getStoreItems),
    switchMap((action) => {
      return this.apollo.query({
        query: _.cloneDeep(action?.query),
        variables: action?.isNotFiltering ? action?.filtering : {filtering: action?.filtering},
        fetchPolicy: 'network-only',
      }).pipe(
        map(({data}: any) => {
          const result: any = Object.values(data)[0];
          if (result?.response?.status) {
            if (result?.page) {
              this.store.dispatch(StoreItemActions.setStoreItemPagination({
                page: result?.page, entity: action?.paginationEntity ?? StoreItemEntity.pagination
              }))
            }
            const isArray = Array.isArray(result?.data);
            this.store.dispatch(StoreItemActions.loadStoreItems({
              storeItems: isArray ? result?.data : [result?.data],
              entity: action?.entity
            }));
          } else {
            this.notificationService.errorMessage(result?.response?.message);
          }
        })
      );
    })
  ), {dispatch: false});

  getMergeMap$ = createEffect(() => this.actions$.pipe(
    ofType(StoreItemActions.getStoreItemsMergeMap),
    mergeMap((action) => {
      return this.apollo.query({
        query: _.cloneDeep(action?.query),
        variables: action?.isNotFiltering ? action?.filtering : {filtering: action?.filtering},
        fetchPolicy: 'network-only',
      }).pipe(
        map(({data}: any) => {
          const result: any = Object.values(data)[0];
          if (result?.response?.status) {
            if (result?.page) {
              this.store.dispatch(StoreItemActions.setStoreItemPagination({
                page: result?.page, entity: action?.paginationEntity ?? StoreItemEntity.pagination
              }))
            }
            const isArray = Array.isArray(result?.data);
            this.store.dispatch(StoreItemActions.loadStoreItems({
              storeItems: isArray ? result?.data : [result?.data],
              entity: action?.entity
            }));
          } else {
            this.notificationService.errorMessage(result?.response?.message);
          }
        })
      );
    })
  ), {dispatch: false});


  constructor(private readonly actions$: Actions,
              private readonly apollo: Apollo,
              private readonly store: Store<AppState>,
              private readonly notificationService: NotificationService,
  ) {
  }
}
